import { useTranslation } from 'react-i18next'
import { useRef } from 'preact/hooks'
import { File, X } from 'lucide-preact'
import { useDeviceDetection } from '@/hooks/useDeviceDetection'

interface FileSelectorProps {
  selectedFile: File | null
  onFileSelect: (file: File | null) => void
  onError: (error: string | null) => void
  validateFile?: (file: File) => string | null
  className?: string
  accept?: string
}

export function FileSelector({
  selectedFile,
  onFileSelect,
  onError,
  validateFile,
  className = '',
  accept,
}: FileSelectorProps) {
  const { t } = useTranslation()
  const { isMobile } = useDeviceDetection()
  const inputRef = useRef<HTMLInputElement | null>(null)

  const handleFileSelect = (e: Event) => {
    const target = e.target as HTMLInputElement
    if (target.files?.[0]) {
      const file = target.files[0]
      const validationError = validateFile ? validateFile(file) : null
      if (validationError) {
        onError(validationError)
        // clear the input so the same file can be selected again
        if (inputRef.current) inputRef.current.value = ''
        return
      }

      onError(null)
      onFileSelect(file)
    }
  }

  const handleRemoveFile = () => {
    onFileSelect(null)
    // clear hidden input so same file can be uploaded again after removal
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const files = e.dataTransfer?.files
    if (files?.[0]) {
      const file = files[0]
      const validationError = validateFile ? validateFile(file) : null

      if (validationError) {
        onError(validationError)
        // clear the input so the same file can be selected again
        if (inputRef.current) inputRef.current.value = ''
        return
      }

      onError(null)
      onFileSelect(file)
    }
  }

  return (
    <>
      {!selectedFile && (
        <div
          className={`relative border-2 border-dashed border-[#c8d9ff] rounded-3xl p-8 text-center ${className}`}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className='mb-1'>
            <svg
              className='mx-auto h-[71px] w-[71px] text-[#569ff4]'
              fill='none'
              viewBox='0 0 71 71'
              stroke='currentColor'
              strokeWidth={1.5}
            >
              <rect x='4' y='1' width='64' height='64' rx='16' fill='#e8f0ff' />
              <path
                d='M35.5 45V25M28 32.5L35.5 25L43 32.5'
                stroke='#569ff4'
                strokeWidth={2}
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M45 45H26'
                stroke='#569ff4'
                strokeWidth={2}
                strokeLinecap='round'
              />
            </svg>
          </div>
          <div className='text-[#1165ff] font-medium text-[14px] mb-0.5'>
            {t('documentAdd.fileSelector.selectFromDisk')}
          </div>
          {!isMobile && (
            <div className='text-[#569ff4] text-[13px]'>
              {t('documentAdd.fileSelector.dropFile')}
            </div>
          )}
          <input
            type='file'
            ref={inputRef}
            onChange={handleFileSelect}
            className='absolute inset-0 w-full h-full opacity-0 cursor-pointer'
            aria-label={t('documentAdd.fileSelector.ariaLabel')}
            {...(accept && { accept })}
          />
        </div>
      )}

      {selectedFile && (
        <div className='px-10 pb-10'>
          <div className='border border-[#e0e7ff] rounded-3xl px-6 py-4'>
            <div className='flex items-center gap-4'>
              <div className='w-[42px] h-[55px] rounded-3xl flex items-center justify-center'>
                <File className='w-8 h-10 text-cencert-blue' />
              </div>
              <div className='flex-1'>
                <div className='font-semibold text-primary-500 text-[14px] break-all'>
                  {selectedFile.name}
                </div>
              </div>
              <button
                onClick={handleRemoveFile}
                className='w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center flex-shrink-0 text-slate-500 hover:text-slate-600 transition-colors'
                aria-label={t('documentAdd.fileSelector.removeFile')}
              >
                <X className='w-4 h-4' />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
